/**
 * WorkflowBuilderPage - Construtor BPMN do Workflow 2.0 (tela cheia)
 *
 * Canvas limpo, sem paleta lateral: os blocos são criados pelos botões "+"
 * das linhas e dos próprios blocos. O inspetor abre automaticamente ao
 * selecionar um bloco/linha e fecha ao clicar no fundo do mapa. Toda
 * persistência ocorre via RPCs seguras — esta camada é só apresentação.
 *
 * @version 3.7.0
 * RELEASE: 2026-09-04 v3.7.0 - "+" do cartão de decisão/paralelo abre a lista
 *          de blocos e cria a ramificação já com o bloco escolhido (sem bloco
 *          temporário intermediário); inspetor abre no bloco recém-criado.
 * RELEASE: 2026-08-31 v3.1.0 - Inspetor como cartão suspenso sobre o mapa e
 *          exclusão real pelo teclado (Delete) delegada às RPCs.
 * @version 3.0.0
 * RELEASE: 2026-08-31 v3.0.0 - Remoção da paleta lateral, canvas em largura
 *          total e inspetor sob demanda (abre ao selecionar).
 * RELEASE: 2026-08-31 v2.0.0 - Tela cheia real, auto-organização horizontal,
 *          painéis recolhíveis (Sheet no mobile) e saídas configuradas no bloco.
 * RELEASE: 2026-08-30 v1.0.0 - Criação inicial (designer v2)
 */

import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import {
  Activity,
  ArrowLeft,
  Loader2,
  Rocket,
  AlertTriangle,
  FlaskConical,
  LayoutGrid,
  Settings,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { StatusDotLabel } from '@/components/ui/status-dot-label';
import { Sheet, SheetContent, SheetTitle } from '@/components/ui/sheet';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { WorkflowCanvas, EVENTO_APROVADO, EVENTO_REPROVADO } from '@/components/workflow/WorkflowCanvas';
import { WorkflowInspector } from '@/components/workflow/WorkflowInspector';
import type { WorkflowPaletteItem } from '@/components/workflow/workflowNodeMeta';
// v3.2.0: suporte a blocos temporários de fluxos antigos (convertidos ao
// escolher o tipo pelo "+" da linha)
import { ehPlaceholder } from '@/components/workflow/bpmn/bpmnPlaceholder';
import { WorkflowSimulatorDialog } from '@/components/workflow/WorkflowSimulatorDialog';
// Painel de observabilidade: log de todas as execuções do processo
import { WorkflowExecutionsDialog } from '@/components/workflow/WorkflowExecutionsDialog';
import { WorkflowSettingsDialog } from '@/components/workflow/WorkflowSettingsDialog';
import { useWorkflowEditor, useWorkflowOverview } from '@/hooks/useWorkflowDesigner';
import { createLogger } from '@/lib/logger';
import { usePermissions } from '@/hooks/usePermissions';
import { useIsMobile } from '@/hooks/use-mobile';
import { toast } from 'sonner';


const logger = createLogger('WorkflowBuilderPage');

/**
 * Traduz a âncora usada no bloco para o desfecho gravado na linha.
 * Tarefa humana de aprovação: "aprovado" / "reprovado".
 */
function desfechoDaAncora(handle?: string | null): { label: string; event_code: string } | null {
  if (handle === 'aprovado') return { label: 'Aprovado', event_code: EVENTO_APROVADO };
  if (handle === 'reprovado') return { label: 'Reprovado', event_code: EVENTO_REPROVADO };
  return null;
}


export default function WorkflowBuilderPage() {
  const { definitionId } = useParams<{ definitionId: string }>();
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const { editor, isLoading, upsertNode, upsertEdge, deleteNode, deleteEdge, saveCanvas, publish } =
    useWorkflowEditor(definitionId);

  // Fase 6: permissões granulares do módulo (admins recebem tudo pelo contexto)
  const { canEdit, canView } = usePermissions();
  const podeEditar = canEdit('workflow_processes');
  const podePublicar = canView('workflow_publish');

  // Fase 8: lista de processos publicados (bloco de subprocesso) e simulador
  const { overview } = useWorkflowOverview();
  const definicoes = (overview?.definitions ?? [])
    .filter((d) => d.id !== definitionId)
    .map((d) => ({ id: d.id, name: d.name, active_version_id: d.active_version_id }));
  const [simuladorAberto, setSimuladorAberto] = useState(false);
  // Modal com o histórico de execuções deste processo (observabilidade)
  const [execucoesAbertas, setExecucoesAbertas] = useState(false);
  // Diálogo de configurações gerais do processo (nome, escopo, política)
  const [configAberto, setConfigAberto] = useState(false);
  // Publicar exige uma decisão explícita sobre registros anteriores.
  const [publicacaoAberta, setPublicacaoAberta] = useState(false);
  const [retroativoAoAbrir, setRetroativoAoAbrir] = useState<'nao' | 'sim'>('nao');

  const [nodeSelecionado, setNodeSelecionado] = useState<string | null>(null);
  const [edgeSelecionada, setEdgeSelecionada] = useState<string | null>(null);

  // Parâmetro ?node=<id>: vindo do painel de Gargalos, abre o workflow já com
  // a etapa selecionada (inspetor aberto) e limpa o parâmetro da URL.
  const [searchParams, setSearchParams] = useSearchParams();

  const nodes = editor?.nodes ?? [];
  const edges = editor?.edges ?? [];

  useEffect(() => {
    const alvo = searchParams.get('node');
    if (!alvo || nodes.length === 0) return;
    if (nodes.some((n) => n.id === alvo)) {
      setNodeSelecionado(alvo);
      setEdgeSelecionada(null);
    }
    // Consome o parâmetro mesmo quando o node não existe mais (etapa removida)
    const prox = new URLSearchParams(searchParams);
    prox.delete('node');
    setSearchParams(prox, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [nodes, searchParams]);

  const node = useMemo(() => nodes.find((n) => n.id === nodeSelecionado) ?? null, [nodes, nodeSelecionado]);
  const edge = useMemo(() => edges.find((e) => e.id === edgeSelecionada) ?? null, [edges, edgeSelecionada]);

  /** O inspetor existe apenas quando há algo selecionado (padrão Monday) */
  const inspetorAberto = !!node || !!edge;

  /** Fecha o inspetor limpando a seleção atual */
  const fecharInspetor = () => {
    setNodeSelecionado(null);
    setEdgeSelecionada(null);
  };

  /**
   * Acrescenta um bloco logo após outro bloco (botão "+" abaixo do bloco).
   * Cria o node e a ligação em um único passo, sem precisar arrastar linhas.
   *
   * v3.6.0: quando o "+" vem de uma âncora nomeada da tarefa humana de
   * aprovação, a linha já nasce com o evento e o rótulo do desfecho.
   */
  const adicionarAposBloco = async (nodeId: string, item: WorkflowPaletteItem, handle?: string | null) => {
    try {
      const origem = nodes.find((n) => n.id === nodeId) ?? null;
      const desfecho = desfechoDaAncora(handle);
      const novoId = await upsertNode.mutateAsync({
        label: item.label,
        node_type: item.nodeType,
        config: item.defaultConfig,
        position_x: (origem?.position_x ?? 80) + (handle === 'reprovado' ? 320 : 0),
        position_y: (origem?.position_y ?? 40) + 220,
        sort_order: nodes.length,
      });

      await upsertEdge.mutateAsync({
        from_node_id: nodeId,
        to_node_id: novoId,
        label: desfecho?.label ?? null,
        event_code: desfecho?.event_code ?? null,
        sort_order: edges.length,
      });


      setEdgeSelecionada(null);
      setNodeSelecionado(novoId);
    } catch (error: any) {
      logger.error('Falha ao acrescentar bloco após o node', { error: error?.message });
    }
  };

  /**
   * Insere um bloco no meio de uma linha existente (botão "+" da linha):
   * a linha original passa a apontar para o novo bloco e é criada uma nova
   * saída do bloco para o destino anterior, preservando rótulo e condições.
   */
  const inserirNaLinha = async (edgeId: string, item: WorkflowPaletteItem) => {
    try {
      const original = edges.find((e) => e.id === edgeId);
      if (!original) return;

      const origem = nodes.find((n) => n.id === original.from_node_id);
      const destino = nodes.find((n) => n.id === original.to_node_id);

      // v3.2.0: se o destino ainda é o bloco temporário criado pelo "+" do
      // gateway, o próprio bloco é convertido no tipo escolhido (não criamos
      // outro bloco nem outra linha).
      if (ehPlaceholder(destino)) {
        await upsertNode.mutateAsync({
          id: destino!.id,
          label: item.label,
          node_type: item.nodeType,
          config: item.defaultConfig,
          position_x: destino!.position_x,
          position_y: destino!.position_y,
          sort_order: destino!.sort_order,
        });
        setEdgeSelecionada(null);
        setNodeSelecionado(destino!.id);
        return;
      }

      const novoId = await upsertNode.mutateAsync({
        label: item.label,
        node_type: item.nodeType,
        config: item.defaultConfig,
        position_x: origem?.position_x ?? destino?.position_x ?? 80,
        position_y: Math.round(((origem?.position_y ?? 0) + (destino?.position_y ?? 240)) / 2),
        sort_order: nodes.length,
      });

      // 1) Religa a linha original para o novo bloco (mantém rótulo/condições)
      await upsertEdge.mutateAsync({
        id: original.id,
        from_node_id: original.from_node_id,
        to_node_id: novoId,
        label: original.label,
        event_code: original.event_code,
        conditions: original.conditions,
        enforcement: original.enforcement,
        is_default: original.is_default,
        is_regression: original.is_regression,
        sort_order: original.sort_order,
      });

      // 2) Cria a saída do novo bloco para o destino anterior
      await upsertEdge.mutateAsync({
        from_node_id: novoId,
        to_node_id: original.to_node_id,
        sort_order: edges.length,
      });

      setEdgeSelecionada(null);
      setNodeSelecionado(novoId);
    } catch (error: any) {
      logger.error('Falha ao inserir bloco na linha', { error: error?.message });
    }
  };

  /**
   * v3.7.0 - Botão "+" de dentro do gateway: abre a lista de blocos e cria a
   * ramificação já com o bloco escolhido (sem bloco temporário intermediário).
   * A linha nasce com o rótulo "Caminho N", que pode ser renomeado no inspetor.
   */
  const adicionarSaida = async (nodeId: string, item: WorkflowPaletteItem) => {
    try {
      const origem = nodes.find((n) => n.id === nodeId) ?? null;
      const saidasAtuais = edges.filter((e) => e.from_node_id === nodeId);

      const novoId = await upsertNode.mutateAsync({
        label: item.label,
        node_type: item.nodeType,
        config: item.defaultConfig,
        position_x: (origem?.position_x ?? 80) + saidasAtuais.length * 300,
        position_y: (origem?.position_y ?? 40) + 260,
        sort_order: nodes.length,
      });

      await upsertEdge.mutateAsync({
        from_node_id: nodeId,
        to_node_id: novoId,
        label: `Caminho ${saidasAtuais.length + 1}`,
        sort_order: saidasAtuais.length,
      });

      // Seleciona o bloco recém-criado para o inspetor já abrir nele
      setEdgeSelecionada(null);
      setNodeSelecionado(novoId);
    } catch (error: any) {
      logger.error('Falha ao criar ramificação do bloco', { error: error?.message });
    }
  };

  /** Remove uma saída pelo cartão do gateway (lixeirinha) */
  const removerSaida = (edgeId: string) => {
    deleteEdge.mutate(edgeId);
    setEdgeSelecionada((atual) => (atual === edgeId ? null : atual));
  };

  /**
   * v3.4.0 - "Organizar": apenas avisa o canvas. Ele reposiciona os blocos na
   * hora e devolve as posições por `onMoveEnd`, que já grava via saveCanvas.
   */
  const [organizarSinal, setOrganizarSinal] = useState(0);
  const organizarMapa = () => setOrganizarSinal((v) => v + 1);



  /**
   * v3.2.0 - Pendências de publicação.
   * A RPC `workflow_validate_version` devolve a chave `pendencias` com objetos
   * `{ nivel, codigo, mensagem, node_id }`. Antes a tela lia `errors`/`problemas`
   * (que não existem na resposta) e por isso nunca mostrava nada — o usuário via
   * apenas o erro genérico ao publicar. Mantemos os formatos antigos por segurança.
   */
  const validacao = editor?.validation as any;
  const pendencias: Array<{ nivel: string; codigo?: string; mensagem: string; node_id?: string | null }> =
    useMemo(() => {
      if (Array.isArray(validacao?.pendencias)) {
        return validacao.pendencias.map((p: any) => ({
          nivel: String(p?.nivel ?? 'erro'),
          codigo: p?.codigo ?? undefined,
          mensagem: String(p?.mensagem ?? p ?? ''),
          node_id: p?.node_id ?? null,
        }));
      }
      const legado = Array.isArray(validacao?.errors)
        ? validacao.errors
        : Array.isArray(validacao?.problemas)
          ? validacao.problemas
          : [];
      return legado.map((m: any) => ({ nivel: 'erro', mensagem: String(m) }));
    }, [validacao]);

  // Só os erros bloqueiam a publicação; alertas ficam listados como aviso.
  const erros = pendencias.filter((p) => p.nivel === 'erro');
  const alertas = pendencias.filter((p) => p.nivel !== 'erro');
  const problemas = pendencias;


  // Resumo do gatilho configurado no bloco de início (o que abre a esteira)
  const resumoGatilho = useMemo(() => {
    const inicio = nodes.find((n) => n.node_type === 'inicio');
    const codigos: string[] = Array.isArray(inicio?.config?.trigger_events)
      ? (inicio!.config!.trigger_events as string[])
      : [];
    if (codigos.length === 0) return 'não configurado';
    const catalogo = editor?.event_catalog ?? [];
    return codigos.map((code) => catalogo.find((ev) => ev.code === code)?.label ?? code).join(', ');
  }, [nodes, editor]);

  if (isLoading || !editor) {
    return (
      <div className="flex h-dvh items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  /** Propriedades compartilhadas pelo inspetor (desktop e Sheet do mobile) */
  const inspetorProps = {
    node,
    edge,
    nodes,
    edges,
    actionCatalog: editor.action_catalog,
    eventCatalog: editor.event_catalog,
    definitions: definicoes,
    // Entidade-raiz do processo: define quais campos aparecem nos filtros
    entidade: editor?.definition?.root_entity_type ?? null,
    // Conflitos reais de gatilho com outros processos ativos da mesma entidade
    triggerConflicts: editor?.trigger_conflicts ?? [],

    isSaving: upsertNode.isPending || upsertEdge.isPending,
    onSaveNode: (patch: any) =>
      upsertNode.mutate({
        id: patch.id,
        label: patch.label ?? node?.label ?? '',
        node_type: (patch.node_type ?? node?.node_type)!,
        code: node?.code ?? null,
        config: (patch.config ?? node?.config ?? {}) as Record<string, unknown>,
        on_enter_actions: patch.on_enter_actions ?? node?.on_enter_actions ?? [],
        on_complete_actions: node?.on_complete_actions ?? [],
        position_x: node?.position_x ?? null,
        position_y: node?.position_y ?? null,
        sort_order: node?.sort_order ?? null,
      }),
    // v2.5.0: a ligação salva pode não ser a selecionada (Sim/Não do bloco de
    // Condição salva as linhas que saem do node), por isso resolvemos pelo id.
    onSaveEdge: (patch: any) => {
      const alvo = edges.find((e) => e.id === patch.id) ?? edge;
      if (!alvo) return;
      upsertEdge.mutate({
        id: patch.id,
        from_node_id: alvo.from_node_id,
        to_node_id: alvo.to_node_id,
        label: patch.label !== undefined ? patch.label : (alvo.label ?? null),
        event_code: patch.event_code !== undefined ? patch.event_code : (alvo.event_code ?? null),
        conditions: patch.conditions !== undefined ? patch.conditions : (alvo.conditions ?? null),
        enforcement: alvo.enforcement ?? null,
        is_default: patch.is_default ?? false,
        is_regression: patch.is_regression ?? !!alvo.is_regression,
        sort_order: alvo.sort_order ?? null,
      });
    },

    /**
     * v4.0.0 - O caminho é configurado APENAS no painel da linha.
     * Clicar num caminho da lista do bloco seleciona a aresta correspondente.
     * v4.1.0 - onSaveSaida/onDeleteSaida removidos: a seção "Saídas do bloco"
     * saiu do sheet (WorkflowInspector v2.8.0); a linha é o único editor.
     */
    onSelecionarSaida: (edgeId: string) => {
      setNodeSelecionado(null);
      setEdgeSelecionada(edgeId);
    },

    /**
     * v4.0.0 - Reordena os caminhos do bloco trocando o `sort_order` das duas
     * linhas envolvidas (a ordem importa: o motor segue a primeira compatível).
     */
    onReordenarSaida: (edgeId: string, direcao: 'cima' | 'baixo') => {
      if (!node) return;
      const irmas = edges
        .filter((e) => e.from_node_id === node.id)
        .sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0));
      const indice = irmas.findIndex((e) => e.id === edgeId);
      const alvo = direcao === 'cima' ? indice - 1 : indice + 1;
      if (indice < 0 || alvo < 0 || alvo >= irmas.length) return;

      const salvar = (linha: typeof irmas[number], ordem: number) =>
        upsertEdge.mutate({
          id: linha.id,
          from_node_id: linha.from_node_id,
          to_node_id: linha.to_node_id,
          label: linha.label ?? null,
          event_code: linha.event_code ?? null,
          conditions: linha.conditions ?? null,
          enforcement: linha.enforcement ?? 'allow',
          is_default: !!linha.is_default,
          is_regression: !!linha.is_regression,
          sort_order: ordem,
        });

      salvar(irmas[indice], alvo);
      salvar(irmas[alvo], indice);
    },


    /**
     * v3.5.0 - Cria um bloco novo já ligado a um caminho deste bloco.
     * Usa exatamente o mesmo par de RPCs do "+" do mapa
     * (`upsert_workflow_node` + `upsert_workflow_edge`), sem lógica duplicada.
     */
    onCriarDestino: async (item: WorkflowPaletteItem, patch?: any) => {
      if (!node) return;
      try {
        const novoId = await upsertNode.mutateAsync({
          label: item.label,
          node_type: item.nodeType,
          config: item.defaultConfig,
          position_x: (node.position_x ?? 80) + (patch?.is_default ? 260 : 0),
          position_y: (node.position_y ?? 40) + 220,
          sort_order: nodes.length,
        });

        const atual = patch?.id ? edges.find((e) => e.id === patch.id) : null;
        await upsertEdge.mutateAsync({
          id: patch?.id ?? null,
          from_node_id: node.id,
          to_node_id: novoId,
          label: patch?.label ?? atual?.label ?? null,
          event_code: patch?.event_code ?? atual?.event_code ?? null,
          conditions: patch?.conditions ?? atual?.conditions ?? null,
          enforcement: atual?.enforcement ?? 'allow',
          is_default: patch?.is_default ?? false,
          is_regression: patch?.is_regression ?? false,
          sort_order: atual?.sort_order ?? edges.length,
        });

        setEdgeSelecionada(null);
        setNodeSelecionado(novoId);
      } catch (error: any) {
        logger.error('Falha ao criar bloco de destino do caminho', { error: error?.message });
      }
    },

    onDeleteNode: (id: string) => {
      deleteNode.mutate(id);
      setNodeSelecionado(null);
    },
    onDeleteEdge: (id: string) => {
      deleteEdge.mutate(id);
      setEdgeSelecionada(null);
    },
  };

  return (
    <div className="flex h-dvh w-full flex-col bg-background">
      {/* Barra superior */}
      <div className="flex items-center gap-2 border-b bg-card px-3 py-2">
        <Button variant="ghost" size="icon" onClick={() => navigate('/tenant/workflow')}>
          <ArrowLeft className="h-4 w-4" />
        </Button>

        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <p className="truncate text-sm font-medium text-foreground">{editor.definition.name}</p>
            {/* Status logo após o nome, no padrão bolinha colorida + palavra
                (mesmo modelo da listagem de categorias) */}
            <StatusDotLabel
              className="hidden sm:inline-flex"
              tone={
                editor.definition.status === 'active'
                  ? 'success'
                  : editor.definition.status === 'archived'
                    ? 'neutral'
                    : 'warning'
              }
              label={
                editor.definition.status === 'active'
                  ? 'Ativo'
                  : editor.definition.status === 'archived'
                    ? 'Desativado'
                    : 'Rascunho'
              }
            />
          </div>
          <p className="truncate text-xs text-muted-foreground">
            {nodes.length} blocos · {edges.length} caminhos · Gatilho: {resumoGatilho}
          </p>
        </div>

        <Button variant="outline" size="sm" onClick={organizarMapa} disabled={!podeEditar}>
          <LayoutGrid className="mr-1 h-4 w-4" /> Organizar
        </Button>

        {problemas.length > 0 && (
          <Popover>
            <PopoverTrigger asChild>
              <Button
                variant="outline"
                size="sm"
                className={erros.length > 0 ? 'gap-1 text-destructive' : 'gap-1 text-warning'}
              >
                <AlertTriangle className="h-3.5 w-3.5" /> {problemas.length}
              </Button>
            </PopoverTrigger>
            <PopoverContent align="end" className="w-96">
              {erros.length > 0 && (
                <>
                  <p className="mb-2 text-sm font-medium">Impedem a publicação</p>
                  <ul className="mb-3 space-y-1 text-xs">
                    {erros.map((p, i) => (
                      <li key={`erro-${i}`}>
                        {/* Clicar leva direto ao bloco com problema */}
                        <button
                          type="button"
                          className="text-left text-muted-foreground underline-offset-2 hover:text-foreground hover:underline"
                          onClick={() => p.node_id && setNodeSelecionado(p.node_id)}
                        >
                          {p.mensagem}
                        </button>
                      </li>
                    ))}
                  </ul>
                </>
              )}

              {alertas.length > 0 && (
                <>
                  <p className="mb-2 text-sm font-medium">Avisos (não impedem publicar)</p>
                  <ul className="space-y-1 text-xs">
                    {alertas.map((p, i) => (
                      <li key={`alerta-${i}`}>
                        <button
                          type="button"
                          className="text-left text-muted-foreground underline-offset-2 hover:text-foreground hover:underline"
                          onClick={() => p.node_id && setNodeSelecionado(p.node_id)}
                        >
                          {p.mensagem}
                        </button>
                      </li>
                    ))}
                  </ul>
                </>
              )}
            </PopoverContent>
          </Popover>
        )}


        {/* Configurações gerais do processo (nome, escopo, política) */}
        <Button size="sm" variant="outline" onClick={() => setConfigAberto(true)}>
          <Settings className="mr-1 h-4 w-4" /> Configurações
        </Button>

        {/* Histórico de execuções do processo (observabilidade) */}
        <Button size="sm" variant="outline" onClick={() => setExecucoesAbertas(true)}>
          <Activity className="mr-1 h-4 w-4" /> Execuções
        </Button>

        <Button size="sm" variant="outline" onClick={() => setSimuladorAberto(true)}>
          <FlaskConical className="mr-1 h-4 w-4" /> Testar
        </Button>


        <Button
          size="sm"
          onClick={() => {
            // v3.2.0: se já sabemos das pendências, avisamos com clareza antes de chamar a RPC
            if (erros.length > 0) {
              toast.error(
                `${erros.length} pendência(s) impedem a publicação. Abra o alerta ao lado para ver e corrigir.`,
              );
              return;
            }
            setPublicacaoAberta(true);
          }}
          disabled={publish.isPending || !podePublicar}
          title={podePublicar ? undefined : 'Sem permissão para publicar processos'}
        >
          {publish.isPending ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : <Rocket className="mr-1 h-4 w-4" />}
          Publicar
        </Button>

      </div>

      {/* Corpo: mapa em largura total e inspetor suspenso sobre o mapa */}
      <div className="relative flex min-h-0 flex-1">
        <div className="min-w-0 flex-1">
          <WorkflowCanvas
            organizarSinal={organizarSinal}
            nodes={nodes}
            edges={edges}
            actionCatalog={editor.action_catalog}
            eventCatalog={editor.event_catalog}
            selectedNodeId={nodeSelecionado}
            selectedEdgeId={edgeSelecionada}
            onSelectNode={(id) => {
              setNodeSelecionado(id);
              setEdgeSelecionada(null);
            }}
            onSelectEdge={(id) => {
              setEdgeSelecionada(id);
              setNodeSelecionado(null);
            }}
            /* v2.7.0: âncora "sim"/"nao" do bloco de Condição já nasce rotulada */
            onConnect={(from, to, handle) => {
              // v3.6.0: âncoras "aprovado"/"reprovado" já gravam o evento do desfecho
              const desfecho = desfechoDaAncora(handle);
              upsertEdge.mutate({
                from_node_id: from,
                to_node_id: to,
                label: desfecho?.label ?? (handle === 'sim' ? 'Sim' : handle === 'nao' ? 'Não' : null),
                event_code: desfecho?.event_code ?? null,
                is_default: handle === 'nao',
                sort_order: edges.length,
              });
            }}


            onMoveEnd={(positions) => saveCanvas.mutate(positions)}
            podeEditar={podeEditar}
            onInsertOnEdge={inserirNaLinha}
            onAppendAfterNode={adicionarAposBloco}
            onAdicionarSaida={adicionarSaida}
            onRemoverSaida={removerSaida}
            onDeleteNode={(id) => {
              deleteNode.mutate(id);
              setNodeSelecionado(null);
            }}
            onDeleteEdge={(id) => {
              deleteEdge.mutate(id);
              setEdgeSelecionada(null);
            }}

          />
        </div>

        {/* Cartão suspenso (bordas arredondadas, sombra) — padrão da referência */}
        {!isMobile && inspetorAberto && (
          <div className="pointer-events-none absolute inset-y-0 right-0 z-10 flex w-[440px] items-stretch p-3">
            <div className="pointer-events-auto w-full overflow-hidden rounded-2xl border bg-card shadow-xl">
              <WorkflowInspector {...inspetorProps} onClose={fecharInspetor} />
            </div>
          </div>
        )}
      </div>

      {/* Configuração em Sheet no mobile */}
      {isMobile && (
        <Sheet open={inspetorAberto} onOpenChange={(aberto) => !aberto && fecharInspetor()}>
          <SheetContent side="right" className="w-[92vw] max-w-[480px] p-0">
            <SheetTitle className="sr-only">Configuração do bloco</SheetTitle>
            <WorkflowInspector {...inspetorProps} onClose={fecharInspetor} />
          </SheetContent>
        </Sheet>
      )}

      {/* Simulação em modo seco (Fase 8) */}
      <WorkflowSimulatorDialog
        definitionId={definitionId}
        open={simuladorAberto}
        onOpenChange={setSimuladorAberto}
      />

      {/* Log de execuções deste processo (observabilidade) */}
      <WorkflowExecutionsDialog
        open={execucoesAbertas}
        onOpenChange={setExecucoesAbertas}
        definitionId={definitionId}
        definitionName={editor.definition.name}
      />



      {/* Configurações gerais do processo (edição pós-criação) */}
      <WorkflowSettingsDialog
        open={configAberto}
        onOpenChange={setConfigAberto}
        podeEditar={podeEditar}
        // Seção "Versões": dados já carregados pelo construtor (nenhuma consulta extra)
        versions={editor.versions ?? []}
        activeVersionId={editor.definition.active_version_id}
        draftVersionId={editor.draft_version_id}
        definition={{
          id: editor.definition.id,
          name: editor.definition.name,
          description: editor.definition.description,
          root_entity_type: editor.definition.root_entity_type,
          default_enforcement: editor.definition.default_enforcement,
          workspace_ids: editor.definition.workspace_ids,
        }}
      />

      {/* A publicação não executa lote: esta escolha só controla a próxima abertura/evento. */}
      <Dialog open={publicacaoAberta} onOpenChange={setPublicacaoAberta}>
        <DialogContent className="sm:max-w-[520px]" preventCloseOnOutsideInteraction>
          <DialogHeader>
            <DialogTitle>Como aplicar esta versão?</DialogTitle>
            <DialogDescription>
              Escolha se negócios anteriores poderão ser avaliados quando forem abertos. Nenhuma fila ou
              reprocessamento em lote será iniciado agora.
            </DialogDescription>
          </DialogHeader>

          <RadioGroup
            value={retroativoAoAbrir}
            onValueChange={(valor) => setRetroativoAoAbrir(valor === 'sim' ? 'sim' : 'nao')}
            className="gap-3 py-2"
          >
            <label className="flex cursor-pointer gap-3 rounded-md border p-3 hover:bg-muted/40">
              <RadioGroupItem value="nao" className="mt-0.5" />
              <span>
                <span className="block text-sm font-medium text-foreground">Somente novos eventos</span>
                <span className="mt-0.5 block text-xs text-muted-foreground">
                  Negócios anteriores só serão avaliados quando ocorrer um evento configurado neste processo.
                </span>
              </span>
            </label>

            <label className="flex cursor-pointer gap-3 rounded-md border p-3 hover:bg-muted/40">
              <RadioGroupItem value="sim" className="mt-0.5" />
              <span>
                <span className="block text-sm font-medium text-foreground">Avaliar existentes ao abrir</span>
                <span className="mt-0.5 block text-xs text-muted-foreground">
                  Ao abrir um negócio anterior, o sistema verifica as condições e cria a instância se forem atendidas.
                </span>
              </span>
            </label>
          </RadioGroup>

          <DialogFooter>
            <Button variant="outline" onClick={() => setPublicacaoAberta(false)} disabled={publish.isPending}>
              Cancelar
            </Button>
            <Button
              onClick={() =>
                publish.mutate(
                  { retroactiveOnAccess: retroativoAoAbrir === 'sim' },
                  { onSuccess: () => setPublicacaoAberta(false) },
                )
              }
              disabled={publish.isPending}
            >
              {publish.isPending ? (
                <Loader2 className="mr-1 h-4 w-4 animate-spin" />
              ) : (
                <Rocket className="mr-1 h-4 w-4" />
              )}
              Confirmar publicação
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

    </div>
  );
}
